import { useState } from "react";
import { Star, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

export default function RatingModal({ ride, role = "passenger", onClose, onRated }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!ride) return null;
  const target = role === "passenger" ? ride.driver_name : ride.client_name;

  const submit = async () => {
    if (stars < 1) { toast.error("Tap a star to rate"); return; }
    setSubmitting(true);
    try {
      await api.post(`/chauffeur/rides/${ride.id}/rate`, { stars, comment });
      toast.success("Thank you for your feedback");
      onRated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit rating");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="rating-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-luxury-black/80 backdrop-blur-sm p-4">
      <div className="bg-luxury-black border border-gold/30 rounded-md max-w-md w-full p-6 relative">
        <button onClick={onClose} data-testid="rating-close" className="absolute top-3 right-3 text-white/40 hover:text-white">
          <X size={18} />
        </button>
        <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Trip Complete</p>
        <h3 className="font-heading text-2xl text-white mb-1">Rate your {role === "passenger" ? "driver" : "passenger"}</h3>
        <p className="text-white/50 text-sm font-body mb-5">{target || "—"}</p>
        <div className="flex justify-center gap-2 mb-5" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`star-${n}`} onClick={() => setStars(n)}
              onMouseEnter={() => setHover(n)}
              className="p-1 transition-transform hover:scale-110">
              <Star
                size={36}
                className={`${(hover || stars) >= n ? "fill-gold text-gold" : "text-white/30"} transition-colors`}
              />
            </button>
          ))}
        </div>
        <Textarea data-testid="rating-comment" value={comment} onChange={(e) => setComment(e.target.value)}
          maxLength={280} rows={3} placeholder="Optional comment (max 280 chars)"
          className="bg-white/5 border-white/15 text-white" />
        <button data-testid="rating-submit" onClick={submit} disabled={submitting || stars < 1}
          className="mt-4 w-full bg-gold text-luxury-black py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-40">
          {submitting ? "Submitting…" : "Submit Rating"}
        </button>
      </div>
    </div>
  );
}
